/**
 * Announcements, client side: what is live for me, and what I did about it.
 *
 * Administrators write them in Administration → Announcements; everybody else
 * meets them as the strip under the header and a centred pop-up. This module
 * holds both queues and the one "open a pop-up now" slot the release-notes menu
 * and the admin preview use.
 *
 * **Polling.** Every five minutes while the tab is visible, and the moment it
 * comes back to the front - a maintenance warning is time-sensitive, but not to
 * the second, and nobody needs one request a minute for it. With no database
 * the server says `available: false` and nothing is ever shown.
 *
 * **Seen and dismissed are remembered on the server**, so a pop-up shown once is
 * shown once per person, not per browser. A pop-up the administrator did not
 * mark "once" comes back each visit - "visit" being this browser tab's session.
 */

import { Atom } from './atom.ts';
import { getLiveAnnouncements, getReleaseNotes, sendAnnouncementReceipt } from '../api/endpoints.ts';
import type { Announcement } from '../api/types.ts';

const POLL_MS = 5 * 60_000;
const SESSION_KEY = 'neptune_announcements_seen_session';

export const announcementsAvailable = new Atom<boolean>(false);
export const liveBanners = new Atom<Announcement[]>([]);
export const pendingPopups = new Atom<Announcement[]>([]);
export const releaseNotesCount = new Atom<number>(0);

/**
 * A pop-up opened on purpose rather than by arrival: "What's new" from the
 * account menu, "Details" on a banner, or Preview in Administration. `list` is
 * what the pager steps through; `preview` means nothing is recorded.
 */
export interface OpenedAnnouncement {
  list: Announcement[];
  index: number;
  preview?: boolean;
}
export const openedAnnouncement = new Atom<OpenedAnnouncement | null>(null);

let timer: ReturnType<typeof setInterval> | null = null;

function sessionSeen(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function rememberSessionSeen(id: string): void {
  const seen = sessionSeen();
  seen.add(id);
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...seen]));
  } catch {
    // Storage refused: the pop-up may come back on the next poll this visit.
  }
}

/** Fetch once. Never throws: an announcement that cannot load is not an error to show. */
export async function refreshAnnouncements(): Promise<void> {
  try {
    const res = await getLiveAnnouncements();
    announcementsAvailable.set(res.available);
    liveBanners.set(res.banners);
    releaseNotesCount.set(res.release_notes ?? 0);
    const seen = sessionSeen();
    pendingPopups.set(res.popups.filter((a) => !seen.has(a.id)));
  } catch {
    // Offline or restarting: the next tick tries again.
  }
}

function tick(): void {
  if (document.visibilityState === 'visible') void refreshAnnouncements();
}

/** Start looking. Idempotent. */
export function startAnnouncementPolling(): void {
  if (timer !== null) return;
  void refreshAnnouncements();
  timer = setInterval(tick, POLL_MS);
  document.addEventListener('visibilitychange', tick);
}

/** Hide a banner now; the server remembers it for next time. */
export function dismissBanner(id: string): void {
  liveBanners.set(liveBanners.value.filter((a) => a.id !== id));
  void sendAnnouncementReceipt(id, { banner_dismissed: true }).catch(() => {});
}

/** The arriving pop-up was closed: take it off the queue and remember it. */
/**
 * Resolves once the receipt is sent (or failed), so a caller about to reload
 * can wait for it - otherwise a show-once pop-up would come back on the new
 * page.
 */
export function markPopupSeen(announcement: Announcement): Promise<void> {
  pendingPopups.set(pendingPopups.value.filter((a) => a.id !== announcement.id));
  rememberSessionSeen(announcement.id);
  if (!announcement.popup_once) return Promise.resolve();
  return sendAnnouncementReceipt(announcement.id, { popup_seen: true }).then(() => undefined, () => undefined);
}

export function openAnnouncement(announcement: Announcement, preview = false): void {
  openedAnnouncement.set({ list: [announcement], index: 0, preview });
}

export function closeOpenedAnnouncement(): void {
  openedAnnouncement.set(null);
}

/**
 * "What's new": every release note this person may read, newest first.
 * Resolves false when there are none, so the caller can say so.
 */
export async function openReleaseNotes(): Promise<boolean> {
  try {
    const { notes } = await getReleaseNotes();
    if (!notes.length) return false;
    openedAnnouncement.set({ list: notes, index: 0 });
    return true;
  } catch {
    return false;
  }
}
